import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiUpload,
  FiPlus,
  FiFileText,
  FiFolder,
  FiChevronDown,
  FiChevronRight,
  FiGlobe,
  FiBookmark,
  FiSearch,
  FiInfo,
  FiDownload,
  FiClipboard,
  FiFile,
} from "react-icons/fi";

const FIXED_CATEGORIES = [
  "CV Upload",
  "Shortlisting",
  "SOP",
  "LOR",
  "Ug_Only",
  "University Specific SOP",
  "Final Documents Upload",
  "University Application Process",
  "Visa Process",
  "Sample Documents",
];

const SAMPLE_DOC_TYPES = ["Undergraduate", "Masters", "PhD"];

const COUNTRIES = [
  "USA",
  "Canada",
  "NZ",
  "France",
  "UK",
  "Germany",
  "Ireland",
  "Spain",
  "Italy",
];

const SAMPLE_DOC_SUBCATEGORIES = ["CV Upload", "SOP", "LOR"];

const DocumentUpload = () => {
  // State variables
  const [activeTab, setActiveTab] = useState("note");
  const [activeCategory, setActiveCategory] = useState(FIXED_CATEGORIES[0]);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [newResourceFile, setNewResourceFile] = useState(null);
  const [resources, setResources] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [countries, setCountries] = useState([...COUNTRIES]);
  const [newCountry, setNewCountry] = useState("");
  const [selectedSampleType, setSelectedSampleType] = useState(
    SAMPLE_DOC_TYPES[0]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [sampleTypes, setSampleTypes] = useState([...SAMPLE_DOC_TYPES]);
  const [newSampleType, setNewSampleType] = useState("");
  const [fetchedSampleTypes, setFetchedSampleTypes] = useState([]);
  const [sampleDocData, setSampleDocData] = useState([]);
  const [selectedSampleSubCategory, setSelectedSampleSubCategory] = useState(
    SAMPLE_DOC_SUBCATEGORIES[0]
  );
  const [showDropzone, setShowDropzone] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const BASE_URL =
    process.env.REACT_APP_BASE_URL || `${process.env.REACT_APP_BASE_URL}`;

  // Format category label for display
  const formatDisplayLabel = (label) => {
    if (label === "Ug_Only") return "UG only";
    return label.replace(/_/g, " ");
  };

  /**
   * Collapse duplicate slashes and strip repeated backend/media/… segments so we never get …/media//media/…
   * (paths relative to MEDIA_ROOT for delete; one segment for building /media/… URLs).
   */
  const normalizeMediaRelativePath = (path) => {
    if (path == null || typeof path !== "string") return path;

    if (path.startsWith("http://") || path.startsWith("https://")) {
      try {
        const u = new URL(path);
        path = u.pathname;
      } catch {
        return path;
      }
    }

    let p = path.replace(/\\/g, "/").replace(/\/+/g, "/").trim();
    while (p.startsWith("/")) p = p.slice(1);

    // Strip longer prefix first; case-insensitive (API sometimes returns Media/…)
    for (;;) {
      if (/^backend\/media\//i.test(p)) {
        p = p.replace(/^backend\/media\//i, "");
      } else if (/^media\//i.test(p)) {
        p = p.replace(/^media\//i, "");
      } else {
        break;
      }
      p = p.replace(/\/+/g, "/");
      while (p.startsWith("/")) p = p.slice(1);
    }

    return p;
  };

  const buildMediaUrl = (pathInput) => {
    const rel = normalizeMediaRelativePath(pathInput);
    if (rel == null || rel === "") return "";
    // If REACT_APP_BASE_URL is …/backend/media, appending /media/ again causes …/media/media/… (links only; delete uses plain names)
    let base = (BASE_URL || "").replace(/\/+$/, "");
    if (/\/media$/i.test(base)) {
      base = base.replace(/\/media$/i, "");
    }
    return `${base}/media/${String(rel).replace(/^\/+/, "")}`;
  };

  /** Same normalization as href; use on click so the opened tab always gets a clean URL (see student DownloadFile.jsx). */
  const openResourceInNewTab = (e, pathInput) => {
    const url = buildMediaUrl(pathInput);
    if (!url) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  /** Encode each path segment for <path:…> routes; do not use encodeURIComponent on the whole path (slashes become %2F and break Django). */
  const encodePathSegments = (path) => {
    if (path == null || path === "") return "";
    return String(path)
      .split("/")
      .filter((s) => s.length > 0)
      .map((seg) => encodeURIComponent(seg))
      .join("/");
  };

  /**
   * DELETE must be .../visa-process-details/<country>/<path>/delete/.
   * If selectedCountry is empty, the URL becomes ...//visa_documents/... which servers collapse to
   * .../visa_documents/... so Django treats "visa_documents" as country (wrong route + GET TypeError).
   */
  const resolveCountryForVisaDelete = (pathForDelete) => {
    const t = (selectedCountry || "").trim();
    if (t) return t;

    const parts = String(pathForDelete || "")
      .replace(/\\/g, "/")
      .split("/")
      .filter((s) => s.length > 0);
    if (parts[0] !== "visa_documents" || !parts[1]) return "";

    const folder = parts[1];
    const norm = (s) => s.toLowerCase().replace(/_/g, " ").trim();
    const hit = countries.find((c) => norm(c) === norm(folder));
    if (hit) return hit;
    return folder.includes("_") ? folder.replace(/_/g, " ") : folder;
  };

  // Get filtered sample resources
  const getSampleResources = () => {
    const currentTypeData = sampleDocData.find(
      (item) => item.student_level === selectedSampleType
    );
    if (!currentTypeData) return [];

    switch (selectedSampleSubCategory) {
      case "CV Upload":
        return currentTypeData.cv_resume || [];
      case "SOP":
        return currentTypeData.sop_doc || [];
      case "LOR":
        return currentTypeData.lor_doc || [];
      case "Ug_Only":
        return currentTypeData.Ug_Only || [];
      default:
        return [];
    }
  };

  // Fetch instructions for selected category
  const fetchInstructions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/categories/${encodeURIComponent(activeCategory)}/`
      );
      setInstructions(response.data.instructions || "");
      setIsLoading(false);
    } catch (error) {
      console.error(
        "Failed to fetch instructions:",
        error.response?.data || error.message
      );
      setIsLoading(false);
    }
  };

  // Fetch resources for the selected category
  const fetchResources = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/resources/${encodeURIComponent(activeCategory)}/`
      );
      setResources(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error(
        "Failed to fetch resources:",
        error.response?.data || error.message
      );
      setIsLoading(false);
    }
  };

  // Fetch sample document types
  const fetchSampleDocTypes = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/resources/api/sample-documents/`
      );
      setSampleDocData(res.data);
      const types = res.data.map((item) => item.student_level);
      setFetchedSampleTypes(types);
      setSampleTypes(types);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch sample document types:", error.message);
      setIsLoading(false);
    }
  };

  // Get all countries for visa process
  const getAll = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/resources/visa-process-details/`
      );
      if (res.data.length > 0) {
        setCountries(res.data.map((item) => item.country_name));
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching visa process details:", error.message);
      setIsLoading(false);
    }
  };

  // Initial effect - fetch data based on selected category
  useEffect(() => {
    if (
      activeCategory !== "Visa Process" &&
      activeCategory !== "Sample Documents"
    ) {
      fetchInstructions();
      fetchResources();
    } else if (activeCategory === "Sample Documents") {
      fetchSampleDocTypes();
    }
  }, [activeCategory]);

  // Effect for Visa Process category
  useEffect(() => {
    if (activeCategory === "Visa Process") {
      fetchVisaProcess();
      getAll();
    }
  }, [selectedCountry, activeCategory, activeTab]);

  // Effect for Sample Documents subcategory change
  useEffect(() => {
    if (selectedSampleSubCategory === "Ug_Only") {
      setSelectedSampleType("Undergraduate");
    }
  }, [selectedSampleSubCategory]);

  // Fetch visa process details
  const fetchVisaProcess = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/resources/visa-process-details/${selectedCountry}/`
      );

      if (activeTab === "note") {
        setInstructions(res.data.notes || "");
      } else {
        setResources(
          Array.isArray(res.data.documents) ? res.data.documents : []
        );
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching visa process details:", error.message);
      setResources([]);
      setIsLoading(false);
    }
  };

  // Add new country
  const handleAddCountry = async () => {
    if (newCountry.trim() && !countries.includes(newCountry.trim())) {
      setIsLoading(true);
      try {
        const res = await axios.post(
          `${BASE_URL}/resources/visa-process-details/`,
          { country_name: newCountry.trim() }
        );

        setCountries((prevCountries) => [...prevCountries, newCountry.trim()]);
        setSelectedCountry(newCountry.trim());

        if (activeTab === "note") {
          setInstructions(res.data.notes || "");
        } else {
          setResources(
            Array.isArray(res.data.documents) ? res.data.documents : []
          );
        }

        setNewCountry("");
        setIsLoading(false);
      } catch (error) {
        console.error("Error adding country:", error.message);
        setIsLoading(false);
      }
    }
  };

  // Save edited instructions
  const handleSaveEdit = async () => {
    setIsLoading(true);
    try {
      if (activeCategory === "Visa Process") {
        await axios.patch(
          `${BASE_URL}/resources/visa-process-details/${selectedCountry}/`,
          { notes: editValue }
        );
      } else {
        await axios.patch(
          `${BASE_URL}/api/categories/${encodeURIComponent(activeCategory)}/`,
          { instructions: editValue }
        );
      }
      setInstructions(editValue);
      setEditing(false);
      setIsLoading(false);
    } catch (error) {
      console.error(
        "Failed to save instructions:",
        error.response?.data || error.message
      );
      setIsLoading(false);
    }
  };

  // Upload a new resource file
  const handleUploadResource = async () => {
    if (newResourceFile) {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", newResourceFile);

      try {
        if (activeCategory === "Visa Process") {
          await axios.patch(
            `${BASE_URL}/resources/visa-process-details/${selectedCountry}/`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          setResources((prev) => [
            ...prev,
            { name: newResourceFile.name, size: newResourceFile.size },
          ]);
        } else if (activeCategory === "Sample Documents") {
          const fieldMap = {
            "CV Upload": "cv_resume",
            SOP: "sop_doc",
            LOR: "lor_doc",
          };

          const fieldName = fieldMap[selectedSampleSubCategory];
          if (!fieldName) return;

          const formData = new FormData();
          formData.append(fieldName, newResourceFile);

          const currentSample = sampleDocData.find(
            (item) => item.student_level === selectedSampleType
          );

          await axios.patch(
            `${BASE_URL}/resources/api/sample-documents/${currentSample.id}/`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          await fetchSampleDocTypes();
        } else {
          await axios.post(
            `${BASE_URL}/api/resources/${encodeURIComponent(
              activeCategory
            )}/upload/`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          setResources((prev) => [
            ...prev,
            { name: newResourceFile.name, size: newResourceFile.size },
          ]);
        }

        setNewResourceFile(null);
        setShowDropzone(false);
        setIsLoading(false);

        // Refresh data after upload
        if (activeCategory === "Sample Documents") {
          fetchSampleDocTypes();
        } else if (activeCategory === "Visa Process") {
          fetchVisaProcess();
        } else {
          fetchResources();
        }
      } catch (error) {
        console.error(
          "Failed to upload resource:",
          error.response?.data || error.message
        );
        setIsLoading(false);
      }
    }
  };

  // Delete a resource (pass full row: string path or { name, file } so path matches DB; name alone can omit media/ prefix)
  const handleDelete = async (resource) => {
    const rawPath =
      typeof resource === "object" && resource !== null
        ? resource.file ?? resource.name
        : resource;
    const displayTarget =
      typeof resource === "object" && resource?.name ? resource : rawPath;

    if (
      !window.confirm(
        `Are you sure you want to delete "${getResourceDisplayName(
          displayTarget
        )}"?`
      )
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const pathForDelete = normalizeMediaRelativePath(rawPath);
      if (!pathForDelete) {
        console.error("Delete: empty path after normalize");
        setIsLoading(false);
        return;
      }

      if (activeCategory === "Visa Process") {
        const countryForDelete = resolveCountryForVisaDelete(pathForDelete);
        if (!countryForDelete) {
          alert(
            "Select a country in the sidebar, or ensure the file path starts with visa_documents/<country>/."
          );
          setIsLoading(false);
          return;
        }
        const encodedPath = encodePathSegments(pathForDelete);
        const encodedCountry = encodeURIComponent(countryForDelete);
        await axios.delete(
          `${BASE_URL}/resources/visa-process-details/${encodedCountry}/${encodedPath}/delete/`
        );
      } else if (activeCategory === "Sample Documents") {
        const encodedResourceName = encodeURIComponent(pathForDelete);
        const currentSample = sampleDocData.find(
          (item) => item.student_level === selectedSampleType
        );

        let selectedSubbyField = "";
        switch (selectedSampleSubCategory) {
          case "CV Upload":
            selectedSubbyField = "cv_resume";
            break;
          case "SOP":
            selectedSubbyField = "sop_doc";
            break;
          case "LOR":
            selectedSubbyField = "lor_doc";
            break;
          default:
            console.warn("Unknown subcategory");
            return;
        }

        await axios.delete(
          `${BASE_URL}/resources/api/sample-documents/${currentSample.id}/${selectedSubbyField}/delete-file/`,
          {
            params: {
              file_path: encodedResourceName,
            },
          }
        );
      } else {
        const encodedCategory = encodeURIComponent(activeCategory);
        const encodedResourceName = encodeURIComponent(pathForDelete);

        await axios.delete(
          `${BASE_URL}/api/resources/${encodedCategory}/${encodedResourceName}/delete/`
        );
      }

      // Refresh data after deletion
      if (activeCategory === "Sample Documents") {
        fetchSampleDocTypes();
      } else if (activeCategory === "Visa Process") {
        fetchVisaProcess();
      } else {
        fetchResources();
      }

      setIsLoading(false);
    } catch (error) {
      console.error(
        "Failed to delete resource:",
        error.response?.data || error.message
      );
      setIsLoading(false);
    }
  };

  // Delete a sample document type
  const handleDeleteSampleType = async (type) => {
    if (
      !window.confirm(`Are you sure you want to delete the type "${type}"?`)
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const item = sampleDocData.find((item) => item.student_level === type);
      if (!item) return;

      await axios.delete(
        `${BASE_URL}/resources/api/sample-documents/${item.id}/`
      );
      await fetchSampleDocTypes();
      setSelectedSampleType(sampleTypes[0] || "");
      setIsLoading(false);
    } catch (error) {
      console.error("Delete error:", error.response?.data || error.message);
      setIsLoading(false);
    }
  };

  // Get display name for resource
  const getResourceDisplayName = (resource) => {
    if (!resource) return "Unknown file";

    if (typeof resource === "object" && resource.name) {
      return resource.name;
    }

    if (typeof resource === "string") {
      try {
        const parts = resource.split(/[/\\]/);
        let filename = parts.pop();

        if (!filename) return resource;

        // Remove known prefixes (e.g., 'sop_', 'cv_', 'lor_')
        const knownPrefixes = ["sop_", "cv_", "lor_"];
        for (const prefix of knownPrefixes) {
          if (filename.startsWith(prefix)) {
            filename = filename.replace(prefix, "");
            break;
          }
        }

        return filename;
      } catch (error) {
        return resource;
      }
    }

    return String(resource);
  };

  // Format file size display
  const formatFileSize = (size) => {
    if (!size) return "N/A";

    const units = ["B", "KB", "MB", "GB"];
    let formattedSize = parseFloat(size);
    let unitIndex = 0;

    while (formattedSize >= 1024 && unitIndex < units.length - 1) {
      formattedSize /= 1024;
      unitIndex++;
    }

    return `${formattedSize.toFixed(1)} ${units[unitIndex]}`;
  };

  // Convert HTML to markup for rendering
  const createMarkup = () => {
    return { __html: instructions };
  };

  // Filter sample types based on subcategory
  const filteredSampleTypes =
    selectedSampleSubCategory === "Ug_Only"
      ? sampleTypes.filter((type) => type === "Undergraduate")
      : sampleTypes;

  // Format sample types for react-select
  const formattedSampleTypes = filteredSampleTypes.map((type) => ({
    value: type,
    label: type,
  }));

  // Filter resources by search term
  const filteredResources =
    activeCategory === "Sample Documents"
      ? getSampleResources().filter((resource) =>
          getResourceDisplayName(resource)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      : resources.filter((resource) =>
          getResourceDisplayName(resource.name || resource)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );

  // Custom react-select Option component with delete button
  const CustomOption = (props) => {
    const { data, innerRef, innerProps } = props;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        className="flex justify-between items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
      >
        <span>{data.label}</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteSampleType(data.value);
          }}
          className="text-red-500 hover:text-red-700"
          title="Delete this type"
        >
          <FiTrash2 size={16} />
        </button>
      </div>
    );
  };

  // Show tooltip
  const showTooltip = (text, e) => {
    setTooltipText(text);
    setTooltipPosition({ x: e.clientX, y: e.clientY });
    setTooltipVisible(true);
  };

  // Hide tooltip
  const hideTooltip = () => {
    setTooltipVisible(false);
  };

  // Get note/instructions tab title based on category
  const getNoteTabTitle = () => {
    if (activeCategory === "Visa Process") {
      return "Notes";
    }
    return "Instructions";
  };

  // Get documents tab title based on category
  const getDocumentsTabTitle = () => {
    if (activeCategory === "Sample Documents") {
      return `${selectedSampleType} - ${formatDisplayLabel(
        selectedSampleSubCategory
      )}`;
    }
    if (activeCategory === "Visa Process") {
      return "Important Documents";
    }
    return "Resources";
  };

  // Handle loading the appropriate data based on tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);

    // Load appropriate data based on tab
    if (activeCategory === "Visa Process") {
      fetchVisaProcess();
    } else if (activeCategory === "Sample Documents") {
      if (tab === "note") {
        // No specific action for notes tab in Sample Documents
      } else {
        fetchSampleDocTypes();
      }
    } else {
      if (tab === "note") {
        fetchInstructions();
      } else {
        fetchResources();
      }
    }
  };

  const handleDeleteCountry = async (countryName) => {
    if (!window.confirm(`Delete country "${countryName}"?`)) return;

    setIsLoading(true);
    try {
      await axios.delete(
        `${BASE_URL}/resources/visa-process-details/${countryName}/`
      );
      setCountries((prev) => prev.filter((c) => c !== countryName));
      if (selectedCountry === countryName) {
        setSelectedCountry(countries[0] || "");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to delete country:", error.message);
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Tooltip */}
      {tooltipVisible && (
        <div
          className="fixed bg-gray-800 text-white px-3 py-2 rounded text-sm z-50"
          style={{
            left: `${tooltipPosition.x + 10}px`,
            top: `${tooltipPosition.y + 10}px`,
          }}
        >
          {tooltipText}
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl flex flex-col items-center m-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin mb-4"></div>
            <div className="text-gray-700 text-sm sm:text-base">Loading...</div>
          </div>
        </div>
      )}

      <div className="container mx-auto py-4 sm:py-6 px-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center">
          <FiFileText className="mr-2" /> Document Management
        </h1>

        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
          {/* Left Sidebar */}
          <div className="lg:w-1/4 w-full">
            <div className="bg-white rounded-lg shadow-md p-3 sm:p-5 mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 flex items-center">
                <FiFolder className="mr-2" /> Categories
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
                {FIXED_CATEGORIES.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`text-left px-3 sm:px-4 py-2 sm:py-2.5 rounded-md transition-colors duration-200 flex items-center text-sm sm:text-base truncate ${
                      activeCategory === category
                        ? "bg-blue-100 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {activeCategory === category ? (
                      <FiChevronDown className="mr-2 flex-shrink-0" />
                    ) : (
                      <FiChevronRight className="mr-2 flex-shrink-0" />
                    )}
                    <span className="truncate">
                      {formatDisplayLabel(category)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Visa Process Country Selector */}
            {activeCategory === "Visa Process" && (
              <div className="bg-white rounded-lg shadow-md p-3 sm:p-5">
                <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 flex items-center">
                  <FiGlobe className="mr-2" /> Country Selection
                </h2>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Country
                  </label>
                  <ul className="space-y-1 max-h-48 overflow-auto border rounded-md p-2 bg-gray-50">
                    {countries.map((country) => (
                      <li
                        key={country}
                        className={`flex justify-between items-center px-3 py-1.5 rounded cursor-pointer ${
                          selectedCountry === country
                            ? "bg-blue-100 text-blue-700"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => setSelectedCountry(country)}
                      >
                        <span>{country}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCountry(country);
                          }}
                          className="text-red-500 hover:text-red-700"
                          title="Delete this country"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Add New Country
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={newCountry}
                      onChange={(e) => setNewCountry(e.target.value)}
                      placeholder="Country name"
                      className="flex-1 p-2.5 border border-gray-300 rounded-l-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleAddCountry}
                      disabled={!newCountry.trim()}
                      className={`flex items-center px-3 rounded-r-md ${
                        newCountry.trim()
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <FiPlus />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sample Documents Type Selector */}
            {activeCategory === "Sample Documents" && (
              <div className="bg-white rounded-lg shadow-md p-3 sm:p-5">
                <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 flex items-center">
                  <FiBookmark className="mr-2" /> Document Type
                </h2>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Document Type
                  </label>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    placeholder="Select or create a type"
                    options={formattedSampleTypes}
                    value={formattedSampleTypes.find(
                      (opt) => opt.value === selectedSampleType
                    )}
                    onChange={(selected) =>
                      setSelectedSampleType(selected ? selected.value : "")
                    }
                    onCreateOption={async (inputValue) => {
                      if (!sampleTypes.includes(inputValue)) {
                        try {
                          setIsLoading(true);
                          const res = await axios.post(
                            `${BASE_URL}/resources/api/sample-documents/`,
                            { student_level: inputValue }
                          );
                          setSelectedSampleType(inputValue);
                          await fetchSampleDocTypes();
                          setIsLoading(false);
                        } catch (error) {
                          console.error(
                            "Error creating new type:",
                            error.message
                          );
                          setIsLoading(false);
                        }
                      }
                    }}
                    components={{ Option: CustomOption }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: "#e2e8f0",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#cbd5e0",
                        },
                      }),
                    }}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Subcategory
                  </label>
                  <select
                    value={selectedSampleSubCategory}
                    onChange={(e) =>
                      setSelectedSampleSubCategory(e.target.value)
                    }
                    className="w-full p-2.5 bg-white border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    {SAMPLE_DOC_SUBCATEGORIES.map((sub) => (
                      <option key={sub} value={sub}>
                        {formatDisplayLabel(sub)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Add New Type
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={newSampleType}
                      onChange={(e) => setNewSampleType(e.target.value)}
                      placeholder="Type name"
                      className="flex-1 p-2.5 border border-gray-300 rounded-l-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      onClick={async () => {
                        const trimmed = newSampleType.trim();
                        if (trimmed && !sampleTypes.includes(trimmed)) {
                          try {
                            setIsLoading(true);
                            const res = await axios.post(
                              `${BASE_URL}/resources/api/sample-documents/`,
                              { student_level: trimmed }
                            );
                            setSelectedSampleType(trimmed);
                            setNewSampleType("");
                            await fetchSampleDocTypes();
                            setIsLoading(false);
                          } catch (error) {
                            console.error(
                              "Error creating new type:",
                              error.response?.data || error.message
                            );
                            setIsLoading(false);
                          }
                        }
                      }}
                      disabled={!newSampleType.trim()}
                      className={`flex items-center px-3 rounded-r-md ${
                        newSampleType.trim()
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <FiPlus />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="lg:w-3/4 w-full">
            {/* Tabs Navigation */}
            <div className="bg-white rounded-t-lg shadow-sm">
              <div className="flex overflow-x-auto border-b border-gray-200">
                {activeCategory !== "Sample Documents" && (
                  <button
                    className={`py-3 px-4 sm:px-6 font-medium text-sm focus:outline-none whitespace-nowrap ${
                      activeTab === "note"
                        ? "border-b-2 border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                    onClick={() => handleTabChange("note")}
                  >
                    <div className="flex items-center">
                      <FiInfo className="mr-1.5" />
                      {getNoteTabTitle()}
                    </div>
                  </button>
                )}
                <button
                  className={`py-3 px-4 sm:px-6 font-medium text-sm focus:outline-none whitespace-nowrap ${
                    activeTab === "documents"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                  onClick={() => handleTabChange("documents")}
                >
                  <div className="flex items-center">
                    <FiFileText className="mr-1.5" />
                    {activeCategory === "Visa Process"
                      ? "Documents"
                      : "Resources"}
                  </div>
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-b-lg shadow-md overflow-hidden p-0 mb-6">
              {/* Instructions/Notes Tab Content */}
              {activeTab === "note" &&
                activeCategory !== "Sample Documents" && (
                  <div>
                    <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 bg-green-50 border-l-4 border-green-500">
                      <h3 className="text-base sm:text-lg font-medium text-green-800">
                        {getNoteTabTitle()}
                      </h3>

                      <div className="flex space-x-2">
                        {!editing ? (
                          <button
                            onClick={() => {
                              setEditValue(instructions);
                              setEditing(true);
                            }}
                            className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 border border-green-700 text-green-700 rounded hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 text-xs sm:text-sm"
                          >
                            <FiEdit2 className="mr-1" /> Edit
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => setEditing(false)}
                              className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-xs sm:text-sm"
                            >
                              <FiX className="mr-1" /> Cancel
                            </button>
                            <button
                              onClick={handleSaveEdit}
                              className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-600 border border-transparent text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-xs sm:text-sm"
                            >
                              <FiSave className="mr-1" /> Save
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="p-4 sm:p-6">
                      {!editing ? (
                        <>
                          {!instructions ? (
                            <div className="text-center py-8 text-gray-500">
                              <FiClipboard className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                              <p>
                                No instructions available for this category.
                              </p>
                              <button
                                onClick={() => {
                                  setEditValue("");
                                  setEditing(true);
                                }}
                                className="mt-2 text-blue-500 hover:underline"
                              >
                                Add instructions
                              </button>
                            </div>
                          ) : (
                            <div
                              className="prose max-w-none ql-editor text-gray-700 break-words overflow-x-auto"
                              dangerouslySetInnerHTML={createMarkup()}
                            ></div>
                          )}
                        </>
                      ) : (
                        <div>
                          <ReactQuill
                            theme="snow"
                            value={editValue}
                            onChange={setEditValue}
                            className="h-64 mb-12"
                            modules={{
                              toolbar: [
                                [{ header: [1, 2, 3, false] }],
                                ["bold", "italic", "underline", "strike"],
                                [{ list: "ordered" }, { list: "bullet" }],
                                [{ color: [] }, { background: [] }],
                                ["link", "image"],
                                ["clean"],
                              ],
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Resources/Documents Tab Content */}
              {activeTab === "documents" && (
                <div>
                  <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 bg-blue-50 border-b border-blue-100">
                    <h3 className="text-base sm:text-lg font-medium text-blue-800 truncate">
                      {getDocumentsTabTitle()}
                    </h3>

                    <button
                      onClick={() => setShowDropzone(!showDropzone)}
                      className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-xs sm:text-sm"
                    >
                      <FiUpload className="mr-1" /> Upload
                    </button>
                  </div>

                  {/* Search and Upload Area */}
                  <div className="p-4 sm:p-6 border-b border-gray-200">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <FiSearch className="mr-1" /> Search Resources
                      </label>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by file name..."
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* File Upload Dropzone */}
                    {showDropzone && (
                      <div className="mt-4 border-2 border-dashed border-gray-300 rounded-md p-4 sm:p-6 bg-gray-50">
                        <div className="text-center">
                          <FiUpload className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                          <div className="mt-2">
                            <p className="text-xs sm:text-sm text-gray-600">
                              {newResourceFile
                                ? `Selected: ${newResourceFile.name}`
                                : "Drag and drop a file, or click to select"}
                            </p>
                          </div>
                          <div className="mt-3 sm:mt-4">
                            <input
                              id="file-upload"
                              type="file"
                              className="sr-only"
                              onChange={(e) =>
                                setNewResourceFile(e.target.files[0])
                              }
                            />
                            <label
                              htmlFor="file-upload"
                              className="px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                            >
                              Select File
                            </label>
                          </div>
                        </div>

                        <div className="mt-3 sm:mt-4 flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setShowDropzone(false);
                              setNewResourceFile(null);
                            }}
                            className="px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUploadResource}
                            disabled={!newResourceFile}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium text-white ${
                              !newResourceFile
                                ? "bg-blue-300 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                            }`}
                          >
                            Upload
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Resources List */}
                  <div className="overflow-auto max-h-96">
                    {filteredResources.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <FiFile className="mx-auto h-12 w-12 text-gray-300" />
                        <p className="mt-2">No documents available</p>
                        <p className="text-sm">
                          Upload your first document to get started
                        </p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {filteredResources.map((resource, index) => {
                          // Handle both object format and string format
                          const resourceName =
                            typeof resource === "object"
                              ? resource.name
                              : resource;
                          const resourceSize =
                            typeof resource === "object" ? resource.size : null;
                          const pathForLink =
                            typeof resource === "object"
                              ? resource.file
                              : resource;
                          const fileUrl = buildMediaUrl(pathForLink);
                          return (
                            <li
                              key={index}
                              className="hover:bg-gray-50 transition-colors"
                              onMouseEnter={(e) =>
                                showTooltip(
                                  `Click to download ${getResourceDisplayName(
                                    resourceName
                                  )}`,
                                  e
                                )
                              }
                              onMouseLeave={hideTooltip}
                            >
                              <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4">
                                <div className="flex-1 min-w-0">
                                  <a
                                    href={fileUrl || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                                    onClick={(e) =>
                                      openResourceInNewTab(e, pathForLink)
                                    }
                                  >
                                    <FiDownload className="mr-2 flex-shrink-0" />
                                    <span className="truncate">
                                      {getResourceDisplayName(resourceName)}
                                    </span>
                                  </a>
                                  {resourceSize && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {formatFileSize(resourceSize)}
                                    </p>
                                  )}
                                </div>

                                <button
                                  onClick={() => handleDelete(resource)}
                                  className="ml-2 sm:ml-4 text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 flex-shrink-0"
                                  title="Delete resource"
                                  onMouseEnter={(e) => {
                                    e.stopPropagation();
                                    showTooltip("Delete this resource", e);
                                  }}
                                  onMouseLeave={hideTooltip}
                                >
                                  <FiTrash2 size={18} />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;
